import React, { useState, useEffect, useRef, useContext } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import { Eye, Loader2, Star, Users } from "lucide-react";

import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { canalDoTicket } from "../../helpers/canalDoTicket";
import { esperaDoTicket, minutosDesde } from "../../helpers/tempoDeEspera";
import { rotuloPresenca } from "../../hooks/usePresence";
import TicketPeekDialog from "../TicketPeekDialog";
import TransferTicketModal from "../TransferTicketModal";
import ResolverTicketModal from "../ResolverTicketModal";
import AceitarTicketModal from "../AceitarTicketModal";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

// Selo sólido e curto, como no fservice: fundo colorido, texto branco em
// caixa alta e 9px. Reconhece-se pela cor antes de ser lido.
const Selo = ({ children, style, className }) => (
  <span
    style={style}
    className={cn(
      "max-w-36 truncate rounded-[3px] px-1 py-[2px] text-[9px] font-bold uppercase leading-none text-white",
      className,
    )}
  >
    {children}
  </span>
);

// Ação da linha: texto pequeno em azul, sem moldura — o mesmo tratamento da
// referência, onde as ações ficam discretas até a pessoa precisar delas.
const Acao = ({ children, onClick, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="rounded px-1.5 py-0.5 text-[11px] font-medium text-[#0872B9] transition-colors hover:bg-[#0872B9]/10 disabled:opacity-40 dark:text-sky-300 dark:hover:bg-sky-300/10"
  >
    {children}
  </button>
);

const cortar = (texto, tamanho) => {
  const t = String(texto || "");

  return t.length > tamanho ? `${t.slice(0, tamanho)}...` : t;
};

// A última mensagem chega crua: localização vem como imagem em base64 e
// contato como VCARD. Mostrar isso na lista não diz nada a quem lê.
const previaDaMensagem = (lastMessage) => {
  const texto = String(lastMessage || "");

  if (texto.includes("data:image/png;base64")) return "Localização";
  if (texto.includes("BEGIN:VCARD")) return "Contato";

  return cortar(texto, 40);
};

const TicketListItem = ({ ticket, presenca }) => {
  // Que data o canto direito mostra. Ver o comentário no rodapé da linha.
  const quandoMostrar =
    ticket.status === "closed" && ticket.closedAt
      ? ticket.closedAt
      : ticket.updatedAt;

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [transferindo, setTransferindo] = useState(false);
  // Encerrar deixou de ser um clique só: o modal pede o motivo e pergunta se
  // o cliente confirma antes.
  const [resolvendo, setResolvendo] = useState(false);
  const [aceitando, setAceitando] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  // Favorito é marcação pessoal: o estado local responde ao clique na hora e
  // só volta atrás se o servidor recusar.
  const [favorito, setFavorito] = useState(Boolean(ticket.favoritado));

  useEffect(() => {
    // O ticket que chega pelo socket é o mesmo para todo mundo e não carrega
    // "favoritado"; sem esta guarda, uma mensagem nova apagaria a estrela.
    if (ticket.favoritado !== undefined) {
      setFavorito(Boolean(ticket.favoritado));
    }
  }, [ticket.favoritado]);

  const alternarFavorito = async (e) => {
    e.stopPropagation();

    const novo = !favorito;
    setFavorito(novo);

    try {
      if (novo) await api.post(`/ticket-favorites/${ticket.id}`);
      else await api.delete(`/ticket-favorites/${ticket.id}`);
    } catch (err) {
      setFavorito(!novo);
      toastError(err);
    }
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const mudarStatus = async (status, userId, irParaOTicket) => {
    setLoading(true);
    try {
      const { data } = await api.put(`/tickets/${ticket.id}`, { status, userId });
      if (irParaOTicket) navigate(`/tickets/${data.id}`);
    } catch (err) {
      toastError(err);
    }
    if (isMounted.current) setLoading(false);
  };

  const isSelected = ticketId && +ticketId === ticket.id;
  const naoLidas = Number(ticket.unreadMessages) || 0;

  const canal = canalDoTicket(ticket);
  const CanalIcone = canal.icone;
  const espera = esperaDoTicket(ticket);

  // Bolinha do avatar: cinza encerrado; verde sem mensagem do cliente
  // pendente; amarelo cliente aguardando; vermelho aguardando há 30+ min.
  const statusDot =
    ticket.status === "closed"
      ? { classe: "bg-gray-400", rotulo: "Encerrado" }
      : naoLidas <= 0
        ? { classe: "bg-green-500", rotulo: "Respondido" }
        : minutosDesde(ticket.updatedAt) >= 30
          ? { classe: "bg-red-500", rotulo: "Cliente esperando há muito tempo" }
          : { classe: "bg-amber-500", rotulo: "Aguardando resposta" };

  // Contato que chegou só com o identificador novo do WhatsApp não tem nome
  // nem telefone; sem isto a linha do atendimento aparece em branco e não dá
  // para saber de quem é a conversa.
  const nomeExibido =
    ticket.contact?.name || ticket.contact?.number || "Contato sem número";

  const initials = (nomeExibido || "?")
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  return (
    <>
      <TransferTicketModal
        modalOpen={transferindo}
        onClose={() => setTransferindo(false)}
        ticketid={ticket.id}
        ticketWhatsappId={ticket.whatsappId}
        ticketQueueId={ticket.queueId}
      />

      <ResolverTicketModal
        open={resolvendo}
        onClose={() => setResolvendo(false)}
        ticket={ticket}
      />

      <AceitarTicketModal
        aberto={aceitando}
        aoFechar={() => setAceitando(false)}
        ticket={ticket}
        aoAceitar={() => navigate(`/tickets/${ticket.id}`)}
      />

      <div
        role="button"
        tabIndex={0}
        className={cn(
          "relative flex cursor-pointer gap-3 border-b px-3 py-2.5 transition-colors hover:bg-accent/60",
          "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
          isSelected && "bg-accent",
        )}
        onClick={() => navigate(`/tickets/${ticket.id}`)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(`/tickets/${ticket.id}`);
          }
        }}
      >
        {/* Faixa da fila na borda, como na referência. */}
        <span
          className="absolute inset-y-0 left-0 w-1"
          style={{ backgroundColor: ticket.queue?.color || "transparent" }}
        />

        <span className="relative shrink-0 self-start">
          <Avatar className="h-[50px] w-[50px]">
            <AvatarImage
              src={ticket?.contact?.profilePicUrl}
              alt={nomeExibido}
            />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          {!ticket.isGroup && (
            <span
              title={statusDot.rotulo}
              aria-label={`Situação: ${statusDot.rotulo}`}
              className={cn(
                "absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-background",
                statusDot.classe,
              )}
            />
          )}
        </span>

        <div className="min-w-0 flex-1">
          {/* Nome, com o ícone do canal antes e o "espiar" depois. */}
          <div className="flex min-w-0 items-center gap-1.5 pr-14">
            {ticket.isGroup && (
              <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}

            <span
              title={canal.rotulo}
              aria-label={`Canal: ${canal.rotulo}`}
              className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                canal.classe,
              )}
            >
              <CanalIcone className="h-2.5 w-2.5" />
            </span>

            <span
              className={cn(
                "min-w-0 truncate text-sm",
                naoLidas > 0 ? "font-bold" : "font-medium",
              )}
            >
              {cortar(nomeExibido, 60)}
            </span>

            <TicketPeekDialog ticket={ticket} />
          </div>

          <p
            className={cn(
              "mt-0.5 truncate pr-14 text-xs",
              naoLidas > 0
                ? "font-bold text-foreground"
                : "text-muted-foreground",
            )}
          >
            {/* Digitando vence a última mensagem: é o que acontece agora. */}
            {rotuloPresenca(presenca) ? (
              <span className="font-medium text-emerald-600 dark:text-emerald-400">
                {rotuloPresenca(presenca)}
              </span>
            ) : ticket.lastMessage ? (
              <MarkdownWrapper>
                {previaDaMensagem(ticket.lastMessage)}
              </MarkdownWrapper>
            ) : ticket.status === "closed" && ticket.closedAt ? (
              /* Encerrado e sem última mensagem — o caso de quem veio de
                 importação ou de fusão de contatos. A linha ficava em branco
                 (um `&nbsp;` só para segurar a altura); aqui ela responde a
                 pergunta que a aba faz: quando isto foi resolvido. */
              <span className="text-muted-foreground">
                Resolvido em {format(parseISO(ticket.closedAt), "dd/MM 'às' HH:mm")}
              </span>
            ) : (
              <>&nbsp;</>
            )}
          </p>

          {/* Canal, conexão, fila e atendente. O canal por extenso porque o
              selo do avatar tem 4px: com WhatsApp, Instagram, Telegram e
              Messenger na mesma lista, é ele que diz onde a conversa está. */}
          <div className="mt-1 flex flex-wrap items-center gap-[3px]">
            <Selo style={{ backgroundColor: canal.corSelo }}>
              {canal.rotulo}
            </Selo>

            {/* Neutro: a cor da marca agora identifica o canal, e repeti-la
                aqui faria os dois selos virarem um borrão só. */}
            {ticket.whatsapp?.name && (
              <Selo style={{ backgroundColor: "#525252" }}>
                {ticket.whatsapp.name}
              </Selo>
            )}

            <Selo style={{ backgroundColor: ticket.queue?.color || "#7c7c7c" }}>
              {ticket.queue?.name || "Sem fila"}
            </Selo>

            {/* De quem é a conversa hoje. Na aba "Do setor" aparece conversa
                de colega e qualquer um pode puxá-la — o rótulo explícito é o
                que evita assumir sem perceber que já tem atendente. */}
            {ticket.user?.name && (
              <Selo
                title={`Atribuído a ${ticket.user.name}`}
                style={{
                  backgroundColor:
                    ticket.userId === user?.id ? "#000000" : "#b45309",
                }}
              >
                {ticket.userId === user?.id
                  ? ticket.user.name
                  : `Atribuído a ${ticket.user.name}`}
              </Selo>
            )}
          </div>

          {ticket.tags?.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-[3px]">
              {ticket.tags.map((tag) => (
                <Selo key={tag.id} style={{ backgroundColor: tag.color }}>
                  {tag.name}
                </Selo>
              ))}
            </div>
          )}

          {/* Ações da linha, alinhadas à direita como na referência. */}
          <div className="mt-1 flex items-center justify-end gap-0.5">
            {espera && (
              <span
                className={cn(
                  "mr-auto text-[11px] font-semibold tabular-nums",
                  espera.classe,
                )}
                title={
                  ticket.status === "pending"
                    ? "Esperando há"
                    : "Sem resposta há"
                }
              >
                {espera.rotulo}
              </span>
            )}

            {loading && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            )}

            {ticket.status === "pending" && !ticket.isGroup && (
              <Acao
                disabled={loading}
                onClick={() => setAceitando(true)}
              >
                {i18n.t("ticketsList.buttons.accept")}
              </Acao>
            )}

            {ticket.status !== "closed" && !ticket.isGroup && (
              <Acao disabled={loading} onClick={() => setTransferindo(true)}>
                Transferir
              </Acao>
            )}

            {(ticket.status === "open" ||
              (ticket.isGroup && ticket.status === "pending")) && (
              <Acao disabled={loading} onClick={() => setResolvendo(true)}>
                Resolver
              </Acao>
            )}

            {ticket.status === "pending" && !ticket.isGroup && (
              <Acao
                disabled={loading}
                onClick={() => mudarStatus("closed", null)}
              >
                Ignorar
              </Acao>
            )}

            {ticket.status === "closed" && (
              <Acao
                disabled={loading}
                onClick={() => mudarStatus("open", user?.id, true)}
              >
                Reabrir
              </Acao>
            )}

            {/* Estrela: marcação só de quem está logado. Área de toque de
                28px para funcionar no celular sem abrir a conversa por engano. */}
            <button
              type="button"
              aria-label={
                favorito ? "Remover dos favoritos" : "Marcar como favorita"
              }
              aria-pressed={favorito}
              title={
                favorito ? "Remover dos favoritos" : "Marcar como favorita"
              }
              onClick={alternarFavorito}
              className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors",
                favorito
                  ? "text-amber-500 hover:bg-amber-500/10"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Star
                className="h-4 w-4"
                fill={favorito ? "currentColor" : "none"}
              />
            </button>
          </div>
        </div>

        {/* Horário e contador ficam no canto superior direito, fora do fluxo,
            para não empurrar o nome nem as ações. */}
        <div className="absolute right-3 top-2.5 flex flex-col items-end gap-1">
          <span
            className={cn(
              "text-[11px] tabular-nums",
              naoLidas > 0
                ? "font-bold text-emerald-600 dark:text-emerald-400"
                : "text-muted-foreground",
            )}
          >
            {/* Num atendimento encerrado a data que importa é a do
                encerramento, não a do último toque: comentar ou etiquetar um
                chamado de junho fazia ele aparecer com a data de hoje no
                histórico. `closedAt` é nulo nos que foram fechados antes de
                existir esse registro — aí vale o comportamento antigo. */}
            {isSameDay(parseISO(quandoMostrar), new Date())
              ? format(parseISO(quandoMostrar), "HH:mm")
              : format(parseISO(quandoMostrar), "dd/MM/yyyy")}
          </span>

          {naoLidas > 0 && (
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[11px] font-bold tabular-nums text-white">
              {naoLidas}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

export default TicketListItem;
